// src/pages/gene.codes/bloods/BloodAnalysiscomponents/ResultRangeBar.tsx
import React, { useMemo } from "react";
import { BloodTestResult } from "../../../../types/lab/BloodTestTypes";
import { calculateResultSeverity, resolveResultRange, RANGE_BAR_EXTENSION } from "../../../../utils/geneCodes/bloodResultUtils";
import { RangeContext } from "../../../../utils/geneCodes/referenceRange";
import RangeSourceTag from "../../../../components/blood/RangeSourceTag";

interface ResultRangeBarProps {
	result: BloodTestResult;
	rangeContext: RangeContext;
	compact?: boolean;
	showLabels?: boolean;
	height?: number;
}

const ResultRangeBar: React.FC<ResultRangeBarProps> = ({ result, rangeContext, compact = false, showLabels = true, height = compact ? 8 : 16 }) => {
	const calculatedData = useMemo(() => {
		// Use the shared resolver so the bar always works from the same bounds as the badge.
		const { numericValue, minValue, maxValue, display, source } = resolveResultRange(result, rangeContext);
		const severityData = calculateResultSeverity(result, rangeContext);

		// If the range is unknown or the value is unparseable, show the "not available" state.
		// Both badge and bar are already in a coherent "Normal/unknown" posture in this case
		// (calculateResultSeverity returns NORMAL when bounds are null).
		if (isNaN(numericValue) || minValue === null || maxValue === null || isNaN(minValue) || isNaN(maxValue)) {
			return { isValid: false, display, source };
		}

		// Use the SAME window extension the badge uses for its dot position, so the
		// dot (severityData.position) and these zone boundaries can never diverge.
		const normalRange = maxValue - minValue;
		const extendedMin = minValue - normalRange * RANGE_BAR_EXTENSION;
		const extendedMax = maxValue + normalRange * RANGE_BAR_EXTENSION;
		const totalRange = extendedMax - extendedMin;

		// Fallbacks below only fire for a degenerate zero-width range (minValue === maxValue);
		// with the shared 0.5 extension the normal band nominally spans 25%–75% of the bar.
		const normalStartPercent = totalRange > 0 ? ((minValue - extendedMin) / totalRange) * 100 : 25;
		const normalEndPercent = totalRange > 0 ? ((maxValue - extendedMin) / totalRange) * 100 : 75;

		// position is already computed identically by calculateResultSeverity — reuse it.
		const position = severityData.position;

		// Build gradient zone boundaries aligned with the badge's deviation thresholds:
		//   MILD     = deviationPercent ≤ 15%  → yellow  (#faad14)
		//   MODERATE = deviationPercent ≤ 30%  → orange  (#ff7a45)
		//   SEVERE   = deviationPercent > 30%  → red     (#ff4d4f)
		//
		// We convert those value-space thresholds to percent-positions on the bar so the
		// zone colours always match the label the badge shows.
		const mildThreshold = normalRange > 0 ? 0.15 * normalRange : 0;
		const moderateThreshold = normalRange > 0 ? 0.30 * normalRange : 0;

		const lowMildBoundary = totalRange > 0 ? ((minValue - mildThreshold - extendedMin) / totalRange) * 100 : normalStartPercent;
		const lowModerateBoundary = totalRange > 0 ? ((minValue - moderateThreshold - extendedMin) / totalRange) * 100 : normalStartPercent;

		const highMildBoundary = totalRange > 0 ? ((maxValue + mildThreshold - extendedMin) / totalRange) * 100 : normalEndPercent;
		const highModerateBoundary = totalRange > 0 ? ((maxValue + moderateThreshold - extendedMin) / totalRange) * 100 : normalEndPercent;

		const gradientStops: string[] = [];

		// Low-side zones (left of normal range)
		if (lowModerateBoundary > 0) {
			gradientStops.push(`#ff4d4f 0%`);
			gradientStops.push(`#ff4d4f ${Math.max(0, lowModerateBoundary).toFixed(2)}%`);
		}

		if (lowModerateBoundary < lowMildBoundary) {
			gradientStops.push(`#ff7a45 ${Math.max(0, lowModerateBoundary).toFixed(2)}%`);
			gradientStops.push(`#ff7a45 ${Math.max(0, lowMildBoundary).toFixed(2)}%`);
		}

		if (lowMildBoundary < normalStartPercent) {
			gradientStops.push(`#faad14 ${Math.max(0, lowMildBoundary).toFixed(2)}%`);
			gradientStops.push(`#faad14 ${normalStartPercent.toFixed(2)}%`);
		}

		// Normal (green) zone
		gradientStops.push(`#52c41a ${normalStartPercent.toFixed(2)}%`);
		gradientStops.push(`#52c41a ${normalEndPercent.toFixed(2)}%`);

		// High-side zones (right of normal range)
		if (normalEndPercent < highMildBoundary) {
			gradientStops.push(`#faad14 ${normalEndPercent.toFixed(2)}%`);
			gradientStops.push(`#faad14 ${Math.min(100, highMildBoundary).toFixed(2)}%`);
		}

		if (highMildBoundary < highModerateBoundary) {
			gradientStops.push(`#ff7a45 ${Math.min(100, highMildBoundary).toFixed(2)}%`);
			gradientStops.push(`#ff7a45 ${Math.min(100, highModerateBoundary).toFixed(2)}%`);
		}

		if (highModerateBoundary < 100) {
			gradientStops.push(`#ff4d4f ${Math.min(100, highModerateBoundary).toFixed(2)}%`);
			gradientStops.push(`#ff4d4f 100%`);
		}

		const backgroundGradient = `linear-gradient(to right, ${gradientStops.join(", ")})`;

		return {
			position,
			minValue,
			maxValue,
			extendedMin,
			extendedMax,
			numericValue,
			normalStartPercent,
			normalEndPercent,
			backgroundGradient,
			isValid: true,
			severityData,
			display,
			source,
		};
	}, [result, rangeContext]);

	if (!calculatedData.isValid) {
		// No comparable numeric range — e.g. a non-numeric value ("B+", ">15%", a blood
		// group) or a reference range we can't parse. Degrade to a plain, calm value
		// rather than the alarming "Range not available" bar.
		// In compact / label-less contexts the caller already renders the value beside the
		// bar, so keep this column quiet (mirrors the desktop Range column's numeric guard).
		if (compact || !showLabels) {
			return null;
		}
		const displayValue = [result.result_value, result.units].filter(Boolean).join(" ").trim();
		return (
			<div style={{ width: "100%" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
					<span style={{ fontSize: "12px", fontWeight: 600 }}>{result.test_name}</span>
					<span className="mg-mono-num" style={{ fontSize: "12px", color: "var(--mg-text-secondary)" }}>
						{displayValue || "—"}
					</span>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: "8px" }}>
					<span style={{ fontSize: "11px", opacity: 0.7 }}>Reference: {calculatedData.display || "—"}</span>
					<RangeSourceTag source={calculatedData.source} labFlag={result.lab_flag} />
				</div>
			</div>
		);
	}

	const { position, minValue, maxValue, normalStartPercent, normalEndPercent, extendedMin, extendedMax, backgroundGradient, severityData, display, source } = calculatedData;

	const getStatusBadge = () => {
		return (
			<span
				style={{
					fontSize: "11px",
					fontWeight: 600,
					padding: "2px 8px",
					borderRadius: "4px",
					background: severityData.tagColor === "error" ? "rgba(255, 77, 79, 0.1)" : severityData.tagColor === "warning" ? "rgba(250, 173, 20, 0.1)" : "rgba(82, 196, 26, 0.1)",
					color: severityData.color,
				}}
			>
				{severityData.label}
			</span>
		);
	};

	return (
		<div style={{ width: "100%" }}>
			{showLabels && !compact && (
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
					<span style={{ fontSize: "12px", fontWeight: 600 }}>{result.test_name}</span>
					<span style={{ fontSize: "12px", opacity: 0.8 }}>
						{result.result_value} {result.units}
					</span>
				</div>
			)}

			<div style={{ position: "relative", width: "100%" }}>
				<div
					style={{
						width: "100%",
						height: `${height}px`,
						borderRadius: `${height / 2}px`,
						background: backgroundGradient,
						opacity: 0.3,
						position: "relative",
						overflow: "hidden",
					}}
				>
					<div
						style={{
							position: "absolute",
							left: `${position}%`,
							top: "50%",
							transform: "translate(-50%, -50%)",
							width: compact ? "12px" : "16px",
							height: compact ? "12px" : "16px",
							borderRadius: "50%",
							background: severityData.color,
							border: "3px solid white",
							boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
							zIndex: 10,
						}}
					/>

					{!compact && (
						<>
							<div
								style={{
									position: "absolute",
									left: `${normalStartPercent}%`,
									top: "50%",
									transform: "translateY(-50%)",
									width: "2px",
									height: `${height + 8}px`,
									background: "#52c41a",
									opacity: 0.6,
								}}
							/>
							<div
								style={{
									position: "absolute",
									left: `${normalEndPercent}%`,
									top: "50%",
									transform: "translateY(-50%)",
									width: "2px",
									height: `${height + 8}px`,
									background: "#52c41a",
									opacity: 0.6,
								}}
							/>
						</>
					)}
				</div>

				{showLabels && !compact && (
					<div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
						<span style={{ fontSize: "10px", opacity: 0.6 }}>{extendedMin.toFixed(1)}</span>
						<span style={{ fontSize: "10px", opacity: 0.8, fontWeight: 500 }}>
							Normal: {minValue.toFixed(1)} - {maxValue.toFixed(1)}
						</span>
						<span style={{ fontSize: "10px", opacity: 0.6 }}>{extendedMax.toFixed(1)}</span>
					</div>
				)}
			</div>

			{showLabels && !compact && (
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
					<span style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<span style={{ fontSize: "11px", opacity: 0.7 }}>Reference: {display || `${minValue.toFixed(1)} - ${maxValue.toFixed(1)}`}</span>
						<RangeSourceTag source={source} labFlag={result.lab_flag} />
					</span>
					{getStatusBadge()}
				</div>
			)}
		</div>
	);
};

export default ResultRangeBar;
